import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag } from 'antd';
import { authService } from '../../services/authService';
import AV from 'leancloud-storage';
import { ROLE_LABELS } from '../../services/authService';

const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        const isAdmin = await authService.hasRole(currentUser, 'admin');
        if (!isAdmin) {
          message.error('无权限访问用户管理');
          return;
        }
        fetchUsers();
      } catch (error) {
        message.error('权限验证失败');
      }
    };
    checkAccess();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const query = new AV.Query(AV.User);
      query.addAscending('createdAt');
      query.include('roles');
      query.limit(1000);
      
      const usersList = await query.find({ useMasterKey: true });
      
      const roleQuery = new AV.Query(AV.Role);
      const allRoles = await roleQuery.find({ useMasterKey: true });
      
      const userRolesMap = new Map();
      for (const role of allRoles) {
        const users = role.getUsers().query();
        const usersInRole = await users.find({ useMasterKey: true });
        for (const user of usersInRole) {
          if (!userRolesMap.has(user.id)) {
            userRolesMap.set(user.id, []);
          }
          userRolesMap.get(user.id).push(role);
        }
      }
      
      const usersWithRoles = usersList.map((user) => ({
        key: user.id,
        username: user.get('username'),
        email: user.get('email'),
        roles: userRolesMap.get(user.id) || [],
        createdAt: user.get('createdAt'),
        user: user
      }));

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Fetch users error:', error);
      message.error('获取用户列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUser = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      username: record.username,
      email: record.email,
      roles: record.roles.map(role => role.get('name'))
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        // Update existing user
        await authService.updateUser(editingUser.user, values);
      } else {
        // Create new user with role
        await authService.register(
          values.username,
          values.password,
          values.email,
          values.role  // Single role instead of array
        );
      }
      message.success(`${editingUser ? '更新' : '创建'}用户成功`);
      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      console.error('User operation error:', error);
      message.error(`${editingUser ? '更新' : '创建'}用户失败: ` + error.message);
    }
  };

  const handleResetPassword = (record) => {
    resetPasswordForm.resetFields();
    
    Modal.confirm({
      title: '重置用户密码',
      content: (
        <Form 
          form={resetPasswordForm} 
          layout="vertical"
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码' }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        try {
          const values = await resetPasswordForm.validateFields();
          await authService.resetUserPassword(record.key, values.newPassword);
          message.success('密码重置成功');
          resetPasswordForm.resetFields();
        } catch (error) {
          message.error('密码重置失败: ' + error.message);
        }
      },
      onCancel() {
        resetPasswordForm.resetFields();
      },
    });
  };

  const handleDeleteUser = (record) => {
    Modal.confirm({
      title: '确认删除用户',
      content: (
        <div>
          <p>确定要删除以下用户吗？此操作不可恢复。</p>
          <p>用户名: {record.username}</p>
          <p>邮箱: {record.email}</p>
        </div>
      ),
      okText: '删除',
      okButtonProps: {
        danger: true,
      },
      cancelText: '取消',
      onOk: async () => {
        try {
          await authService.deleteUser(record.key);
          message.success('用户删除成功');
          fetchUsers(); // Refresh the list
        } catch (error) {
          message.error('删除用户失败: ' + error.message);
        }
      },
    });
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => (
        <Tag color="blue">
          {roles[0] ? ROLE_LABELS[roles[0].get('name')] || roles[0].get('name') : '无角色'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEditUser(record)}>
            编辑
          </Button>
          <Button 
            type="link" 
            danger
            onClick={() => handleDeleteUser(record)}
          >
            删除
          </Button>
          <Button 
            type="link"
            onClick={() => handleResetPassword(record)}
          >
            重置密码
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAddUser}>
          添加用户
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={users} 
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={editingUser ? {
            username: editingUser.username,
            email: editingUser.email,
            role: editingUser.roles[0]?.get('name')  // Get first role only
          } : undefined}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 